import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { date } from '../../utils/format';
import { ErrorNotice, Empty, Loading, PageHeader, Table } from '../../components/ui';

interface Approval {
  id: string;
  control_code: string;
  requested_value: string;
  limit_value: string;
  unit: string;
  reason: string;
  requested_at: string;
  requested_by_name: string;
}

const shown = (value: string, unit: string): string =>
  unit === 'PERCENT' ? `${Number(value).toFixed(2)}%` : unit === 'FINE_GRAM' ? `${Number(value).toFixed(4)} g` : `₹${Number(value).toFixed(2)}`;

export function ApprovalsPage() {
  const client = useQueryClient();
  const [reason, setReason] = useState<Record<string, string>>({});
  const [error, setError] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['approvals'],
    queryFn: () => api.get<{ data: Approval[] }>('/approvals'),
  });

  const decide = useMutation({
    mutationFn: ({ id, decision, why }: { id: string; decision: 'APPROVED' | 'REJECTED'; why?: string }) =>
      api.post(`/approvals/${id}/decide`, { decision, ...(why ? { reason: why } : {}) }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['approvals'] }),
    onError: setError,
  });

  if (isLoading) return <Loading />;

  return (
    <>
      <PageHeader title="Approvals" subtitle="Exceptions waiting on you. Refusing one needs a reason." />
      <ErrorNotice error={error} />

      {!data || data.data.length === 0 ? (
        <Empty message="Nothing is waiting for approval." />
      ) : (
        <Table head={['Asked', 'By', 'Control', 'Limit', 'Requested', 'Reason', '']}>
          {data.data.map((a) => (
            <tr key={a.id}>
              <td className="td">{date(a.requested_at)}</td>
              <td className="td">{a.requested_by_name}</td>
              <td className="td">{a.control_code}</td>
              <td className="td num">{shown(a.limit_value, a.unit)}</td>
              <td className="td num font-medium">{shown(a.requested_value, a.unit)}</td>
              <td className="td">{a.reason}</td>
              <td className="td">
                <div className="flex items-center gap-2">
                  <button
                    className="btn-primary py-1"
                    onClick={() => decide.mutate({ id: a.id, decision: 'APPROVED' })}
                    disabled={decide.isPending}
                  >
                    Allow
                  </button>
                  <input
                    className="field w-40 py-1"
                    placeholder="Reason to refuse"
                    value={reason[a.id] ?? ''}
                    onChange={(e) => setReason((r) => ({ ...r, [a.id]: e.target.value }))}
                  />
                  <button
                    className="btn-ghost py-1"
                    onClick={() => decide.mutate({ id: a.id, decision: 'REJECTED', why: reason[a.id] })}
                    disabled={decide.isPending || (reason[a.id] ?? '').trim().length < 3}
                  >
                    Refuse
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </>
  );
}
